import { merge } from 'lodash';
import { createReducer, createAsyncThunk } from '@reduxjs/toolkit';
import { assertHasProperties } from 'common/assertions';
import { I18n } from 'common/i18n';
import Actions from 'Actions';
import Constants from 'lib/Constants';
import { httpRequest, coreHeaders } from 'services/httpRequest';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import type { StorytellerState } from 'store/StorytellerReduxState';

interface StoryMetadata {
  storyUid: string;
  storyTitle: string;
  storyDescription: string;
  storyTileConfig?: {
    title: string;
    description: string;
  };
}

export interface CoreSavingState {
  isSaveInProgress: boolean;
  lastSaveErrorsByUid: Record<string, string>;
}

interface SaveErrorPayload {
  storyUid: string;
  error: string;
}

interface SaveSuccessPayload {
  storyUid: string;
}

const initialState: CoreSavingState = {
  isSaveInProgress: false,
  lastSaveErrorsByUid: {},
};

function findMetadataError(metadata: StoryMetadata): string | null {
  if (metadata.storyTitle.length >= Constants.CORE_VIEW_NAME_MAX_LENGTH) {
    return I18n.t('editor.settings_panel.displaying_section.errors.title_too_long');
  }
  return null;
}

async function putViewMetadataToCore(newMetadata: StoryMetadata): Promise<void> {
  assertHasProperties(newMetadata, 'storyUid', 'storyTitle', 'storyDescription');

  const url = `/api/views/${newMetadata.storyUid}.json`;

  const { data } = await httpRequest('GET', url, {
    headers: coreHeaders(),
  });

  const metadata = newMetadata.storyTileConfig
    ? merge({}, data.metadata, { tileConfig: newMetadata.storyTileConfig })
    : data.metadata;

  await httpRequest('PUT', url, {
    data: {
      name: newMetadata.storyTitle,
      description: newMetadata.storyDescription,
      metadata,
    },
    headers: coreHeaders(),
  });
}

export const saveStoryMetadata = createAsyncThunk<
  SaveSuccessPayload,
  { storyUid: string; includeTileConfig?: boolean },
  { rejectValue: SaveErrorPayload }
>(
  Actions.STORY_SAVE_METADATA,
  async ({ storyUid, includeTileConfig = false }, { getState, rejectWithValue }) => {
    const state = getState() as StorytellerState;

    const metadata: StoryMetadata = {
      storyUid,
      storyTitle: storySelectors.getStoryTitle(state, storyUid),
      storyDescription: storySelectors.getStoryDescription(state, storyUid),
      // Conditionally include tile config
      ...(includeTileConfig && {
        storyTileConfig: {
          title: storySelectors.getStoryTileTitle(state, storyUid),
          description: storySelectors.getStoryTileDescription(state, storyUid),
        },
      }),
    };

    const error = findMetadataError(metadata);
    if (error) {
      return rejectWithValue({ storyUid, error });
    }

    await putViewMetadataToCore(metadata);
    return { storyUid };
  },
);

//=============================================================================
// Reducer
//=============================================================================
export const coreSavingReducer = createReducer(initialState, (builder) => {
  builder
    // Handle saveStoryMetadata lifecycle
    .addCase(saveStoryMetadata.pending, (state) => {
      state.isSaveInProgress = true;
    })
    .addCase(saveStoryMetadata.fulfilled, (state, action) => {
      state.isSaveInProgress = false;
      delete state.lastSaveErrorsByUid[action.payload.storyUid];
    })
    .addCase(saveStoryMetadata.rejected, (state, action) => {
      state.isSaveInProgress = false;
      if (action.payload) {
        state.lastSaveErrorsByUid[action.payload.storyUid] = action.payload.error;
      } else {
        state.lastSaveErrorsByUid[action.meta.arg.storyUid] = action.error.message || 'Unknown error';
      }
    });
});

export const selectCoreSaving = (state: StorytellerState) => state.coreSaving;

// Specific selectors
export const selectIsSaveInProgress = (state: StorytellerState) => selectCoreSaving(state).isSaveInProgress;

export const selectLastSaveErrorsByUid = (state: StorytellerState) =>
  selectCoreSaving(state).lastSaveErrorsByUid;

// Convenience selector for getting error by story ID
export const selectLastRequestSaveErrorForStory = (state: StorytellerState, storyUid: string) =>
  selectLastSaveErrorsByUid(state)[storyUid] || null;
